import React, { useEffect } from 'react';
import { AppWindow, Bot, Mic, X } from 'lucide-react';
import { t } from '../../lib/i18n';

interface RecordSourceDialogProps {
  isDarkMode: boolean;
  onClose: () => void;
  /** `withTab`: mikrofonun yanında toplantı sekmesinin sesi de alınsın. */
  onChoose: (withTab: boolean) => void;
  /** Bot gönderme penceresini açar. */
  onSendBot: () => void;
}

/**
 * Web uygulamasında kayıt kaynağı. Tarayıcı bilgisayarın sesine erişemiyor;
 * karşı tarafın sesi ancak toplantı bu tarayıcının bir sekmesinde açıksa ve
 * kullanıcı o sekmeyi "sekme sesiyle" paylaşırsa alınabiliyor (Chrome/Edge).
 * Masaüstü uygulamasında bu pencere yok — orada sistem sesi hep kaydediliyor.
 *
 * Seçim düğmesine basılınca kayıt hemen başlıyor: tarayıcı ekran paylaşımı
 * penceresini yalnızca bir tıklamanın içinde açıyor.
 */
export const RecordSourceDialog: React.FC<RecordSourceDialogProps> = ({ isDarkMode, onClose, onChoose, onSendBot }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const option = `w-full flex items-start gap-3 rounded-xl border p-3.5 text-left transition-colors ${
    isDarkMode
      ? 'border-[#3F3F42] bg-[#1E1E22] hover:border-[#3AC8E0] hover:bg-[#232327]'
      : 'border-neutral-200 bg-neutral-50 hover:border-[#46CBE2] hover:bg-white'
  }`;
  const iconBox = `w-9 h-9 shrink-0 rounded-lg flex items-center justify-center ${
    isDarkMode ? 'bg-[#46CBE2]/10 text-[#46CBE2]' : 'bg-[#1DA4BA]/10 text-[#1DA4BA]'
  }`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50 md:px-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="record-source-title"
        className={`w-full md:max-w-md rounded-t-[26px] md:rounded-2xl border border-x-0 border-b-0 md:border p-5 pb-8 md:pb-5 space-y-4 shadow-xl animate-sheet-up md:animate-none ${
          isDarkMode ? 'bg-[#19191C] border-[#3F3F42] text-neutral-200' : 'bg-white border-neutral-200 text-neutral-800'
        }`}
      >
        <div className="flex items-center justify-between">
          <h2 id="record-source-title" className="flex items-center gap-2 text-[14px] font-bold">
            <Mic className="w-4 h-4 text-[#46CBE2]" />
            {t('Neyi kaydedelim?', 'What should we record?')}
          </h2>
          <button type="button" onClick={onClose} aria-label={t('Kapat', 'Close')} className="p-1 rounded text-neutral-500 hover:text-neutral-300">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-2.5">
          <button type="button" autoFocus className={option} onClick={() => onChoose(true)}>
            <span className={iconBox}>
              <AppWindow className="w-4.5 h-4.5" />
            </span>
            <span className="min-w-0">
              <span className="block text-[13.5px] font-semibold">{t('Mikrofon + toplantı sekmesi', 'Microphone + meeting tab')}</span>
              <span className="block text-[12px] leading-relaxed text-neutral-500 mt-0.5">
                {t(
                  'Meet, Zoom ya da Teams bu tarayıcıda açıksa. Açılan pencerede toplantının sekmesini seç ve “Sekme sesini de paylaş” açık kalsın.',
                  'If Meet, Zoom or Teams is open in this browser. In the next window pick the meeting tab and keep “Also share tab audio” on.'
                )}
              </span>
            </span>
          </button>

          <button type="button" className={option} onClick={() => onChoose(false)}>
            <span className={iconBox}>
              <Mic className="w-4.5 h-4.5" />
            </span>
            <span className="min-w-0">
              <span className="block text-[13.5px] font-semibold">{t('Yalnızca mikrofon', 'Microphone only')}</span>
              <span className="block text-[12px] leading-relaxed text-neutral-500 mt-0.5">
                {t(
                  'Yüz yüze toplantılar ve sesli notlar. Toplantı uygulamada açıksa karşı taraf hoparlörden duyulduğu kadar kaydedilir.',
                  'In-person meetings and voice notes. If the call is in a desktop app, the other side is only captured as heard through your speakers.'
                )}
              </span>
            </span>
          </button>
        </div>

        <div className={`flex items-center justify-between gap-3 border-t pt-3.5 ${isDarkMode ? 'border-[#2E2E34]' : 'border-neutral-200'}`}>
          <span className="text-[12px] text-neutral-500">
            {t('Toplantı masaüstü uygulamasında mı?', 'Is the meeting in a desktop app?')}
          </span>
          <button
            type="button"
            onClick={onSendBot}
            className="flex items-center gap-1.5 text-[12.5px] font-semibold text-[#46CBE2] hover:text-[#7BDAEA] transition-colors"
          >
            <Bot className="w-3.5 h-3.5" />
            {t('Bot gönder', 'Send a bot')}
          </button>
        </div>
      </div>
    </div>
  );
};
